import React, { useState, useEffect, ReactElement, useRef } from 'react';
import styled from '@emotion/styled';
import { extractLocationData, geocodeByPlaceId, normalizeLocationDisplay, renderAvatar } from '../common';
import EditableProfileCover from '../atoms/EditableProfileCover';
import ProfilePhoto from '../atoms/profile/ProfilePhoto';
import { areEqual, isDarkMode, ternaryFunc } from '../utils/helpers';
import { auth10tixVerification, tomtomEnabled } from '../config/Master';
import ProfileCompletionProgress from '../components/blocks/ProfileCompletionProgress';
import ProfilePhotoSliderV2 from '../atoms/profile/ProfilePhotoSliderV2';
import MyProfileCoverInfo from './MyProfileCoverInfo';
import { useDispatch, useSelector } from 'react-redux';
import useModal from '../hooks/useModal';
import { useProfile } from '../contexts/ProfileContext';
import { clearErrors } from '../actions/commonActions';
import { resetDiscoverModeAccountsAction } from '../actions/discoverModeActions';
import { updateProfileCover } from '../actions/profileActions';
import { RootState } from '../store';
import { trackEditProfileCookieConsentShown } from '../utils/vysion/privacy';
import useCookieConsentCheck from '../hooks/useCookieConsentCheck';
import breakpoints from '../theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';
import typography from 'theme/typography';
import { getBackgroundTheme } from 'style';

const Cover = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',

  [breakpoints.down(944.05)]: {
    maxWidth: '100%',
  },

  [breakpoints.down(620.05)]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '0px',
    maxWidth: '100%',
  },
});

const InfoContainer = styled('div')({
  paddingLeft: '12px',
  flex: 3,
  zIndex: 3,
  maxWidth: '494px',
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
  justifyContent: 'space-between',
  [breakpoints.down('sm')]: {
    maxWidth: '100%',
  },

  [breakpoints.down(620.05)]: {
    paddingLeft: '0px',
    margin: '0 15px',
    position: 'relative',
    maxWidth: '100%',
    height: '100%',
  },
});

const PhotoWrapperDesktop = styled('div')({
  display: 'block',
  [breakpoints.down(620.05)]: {
    display: 'none',
  },
});

const PhotoWrapperMobile = styled('div')({
  display: 'none',
  [breakpoints.down(620.05)]: {
    display: 'block',
  },
});

const ProfileInfoDesktop = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  padding: '5px 0',

  [breakpoints.down(620.05)]: {
    display: 'none',
  },
});

const ProfileInfoMobile = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  position: 'absolute',
  bottom: 38,
  zIndex: 2,
  width: '100%',
  [breakpoints.down(426.05)]: {
    maxWidth: 'calc(100% - 95px)',
  },
  [breakpoints.down(375.05)]: {
    maxWidth: 275,
  },
});

const PhotoContainer = styled('div')({
  flex: 1,
  maxWidth: '245px',
  minWidth: '245px',
  textAlign: 'center',
  [breakpoints.down(620.05)]: {
    maxWidth: '100%',
  },
});

const CoverContent = styled('div')({
  margin: 0,
});

const ModalText = styled('div')(() => ({
  marginBottom: 12,
  fontWeight: 400,
  fontSize: 15,
  lineHeight: '20px',
  letterSpacing: -0.3,
  color: isDarkMode() ? '#AFB0B1' : '#595959',
}));

interface BaseCoverProps {
  className?: string;
  age?: number;
  location?: string;
  lastLoginTime?: string;
  lastLoginLocation?: string;
  profileImg?: string;
  editable?: boolean;
  onClickPhoto?: () => void;
  onChangeAddress?: (address: string) => void;
  onSelectLocation?: (address: string, placeId: string, details?: any) => void;
  handleSubmit?: (e: React.FormEvent) => void;
  handleCancel?: (e: React.MouseEvent) => void;
  updateUsername?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  displayName?: string;
  myLocation?: string;
  editProfileCover?: boolean;
  toggleEditable?: (e: React.MouseEvent) => void;
  errors?: any;
  myPhotos?: any;
  isVerified?: boolean;
  offer?: any;
  myProfile?: any;
  isUpdatingProfileCover?: boolean;
  isEditableName?: boolean;
  account?: any;
  children?: React.ReactNode;
  isSelfieVerified: boolean;
}

const BaseCover = React.memo((props: BaseCoverProps) => {
  const {
    className,
    age,
    location,
    profileImg,
    editable,
    onClickPhoto,
    onChangeAddress,
    onSelectLocation,
    handleSubmit,
    handleCancel,
    updateUsername,
    displayName,
    myLocation,
    editProfileCover,
    toggleEditable,
    errors,
    myPhotos,
    isVerified = false,
    offer,
    myProfile,
    isUpdatingProfileCover,
    isEditableName,
    isSelfieVerified,
  } = props;

  const defaultPhotoList = [
    {
      order: 1,
      url: profileImg,
      type: 'photo',
      status: 'default',
      private: false,
      privateReal: false,
      total: 0,
    },
  ];

  const photoList = myPhotos?.data?.length ? myPhotos.data : defaultPhotoList;
  const [freshLocation, setFreshLocation] = useState(myLocation);
  const { profileDetailsRef } = useProfile();

  useEffect(() => {
    const { account } = props;

    if (account) {
      const { profile } = account || {};
      const locationFromProps = normalizeLocationDisplay(
        account.city ? account.city : profile?.data?.city,
        account.region ? account.region : profile?.data?.region,
        account.country ? account.country : profile?.data?.country
      );

      setFreshLocation(locationFromProps);
    }
  }, [props.account]);

  const setMyCurrLocation = () => {
    const evaluator = age && editable;
    const output1 = myLocation !== '' && ` • `;
    const output2 = location && ` • `;

    return ternaryFunc(evaluator, output1, output2);
  };

  const renderProfileInfo = () => {
    return (
      <MyProfileCoverInfo
        displayName={displayName}
        isVerified={isVerified}
        age={age}
        auth10tixVerification={auth10tixVerification}
        freshLocation={freshLocation}
        myLocation={myLocation}
        setMyCurrLocation={setMyCurrLocation}
        toggleEditable={toggleEditable}
        editable={editable}
        isSelfieVerified={isSelfieVerified}
      />
    );
  };

  return (
    <Cover className={className}>
      <PhotoWrapperDesktop>
        <PhotoContainer>
          <ProfilePhoto
            imgUrl={profileImg}
            onClick={onClickPhoto}
            offer={offer}
            myProfile={myProfile}
            isOtherProfile={false}
          />
        </PhotoContainer>
      </PhotoWrapperDesktop>
      <PhotoWrapperMobile>
        <ProfilePhotoSliderV2
          photoList={photoList}
          onClick={onClickPhoto}
          offer={offer}
          isOtherProfile={false}
          profileDetailsComponent={
            <ProfileInfoMobile ref={profileDetailsRef}>{!editProfileCover && renderProfileInfo()}</ProfileInfoMobile>
          }
        />
      </PhotoWrapperMobile>

      {editProfileCover || isUpdatingProfileCover || errors?.city ? (
        <EditableProfileCover
          onChangeAddress={onChangeAddress}
          onSelectLocation={onSelectLocation}
          handleSubmit={handleSubmit}
          handleCancel={handleCancel}
          updateUsername={updateUsername}
          displayName={displayName}
          myLocation={myLocation}
          errors={errors}
          myProfile={myProfile}
          isEditableName={isEditableName}
        />
      ) : (
        <InfoContainer className="profile-cover__info">
          <ProfileInfoDesktop>{renderProfileInfo()}</ProfileInfoDesktop>
          {props.children}
        </InfoContainer>
      )}
    </Cover>
  );
});

const MyProfileCover = (props: any): ReactElement => {
  const { account, username, profileImgUrl, editable, isMigration, isUpdatingProfileCover } = props;

  const { profile: nestedProfile } = useSelector((state: RootState) => state.profile);
  const { errors } = useSelector((state: RootState) => state.common);
  const dispatch = useDispatch();
  const [editProfileCover, setEditProfileCover] = useState(false);
  const [displayName, setDisplayName] = useState(username);
  const [myLocation, setMyLocation] = useState('');
  const initialLocation = useRef('');
  const [, setIsSubmitted] = useState(false);
  const updatedFields = useRef({
    username: false,
    location: false,
  });
  const [updatedLocation, setUpdatedLocation] = useState(undefined);
  const [isEditableName, setIsEditableName] = useState(account.can_change_username);
  const { profile } = account ? account : { profile: null };
  const [isNameChanged, setIsNameChanged] = useState(false);

  const [myPhoto, setPhoto] = useState({});
  const { resetModal, confirmSuccessModal } = useModal();
  const { shouldShowModal: shouldShowCookieConsentModal } = useCookieConsentCheck();
  const [locationUpdatedForConsent, setLocationUpdatedForConsent] = useState(false);

  useEffect(() => {
    if (isUpdatingProfileCover) return;

    profile &&
      setMyLocation(
        getLocationDisplay(
          account.city ? account.city : profile.data.city,
          account.region ? account.region : profile.data.region,
          account.country ? account.country : profile.data.country
        )
      );
  }, [account, profile, isUpdatingProfileCover]);

  useEffect(() => {
    if (myLocation && !initialLocation.current) {
      initialLocation.current = myLocation;
    }
  }, [myLocation]);

  useEffect(() => {
    setPhoto({ ...profileImgUrl });
  }, [profileImgUrl]);

  useEffect(() => {
    if (shouldShowCookieConsentModal && locationUpdatedForConsent) {
      try {
        trackEditProfileCookieConsentShown();
      } catch (err) {
        // fail silently - tracking should not block UX
        // eslint-disable-next-line no-console
        console.error('trackEditProfileCookieConsentShown error', err);
      } finally {
        setLocationUpdatedForConsent(false);
      }
    }
  }, [shouldShowCookieConsentModal, locationUpdatedForConsent]);

  const toggleEditable = e => {
    e.stopPropagation();
    setEditProfileCover(!editProfileCover);
  };

  const locationSelectHandler = (address, placeId, details = null) => {
    if (tomtomEnabled && details !== null) {
      setUpdatedLocation(details);
      setMyLocation(address);
    } else {
      geocodeByPlaceId(placeId)
        .then(results => {
          const loc = results[0];

          if (loc && loc.geometry) {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            setUpdatedLocation(loc, address);
            setMyLocation(address);
          }
        })
        .catch(error => {
          console.error('Issue with Geocoding: ', error);
        });
    }

    handleLocationFieldChange(address);
  };

  const handleUpdatedField = (field: 'username' | 'location', value = true) => {
    updatedFields.current = {
      ...updatedFields.current,
      [field]: value,
    };
  };

  const handleResetUpdatedFields = () => {
    updatedFields.current = {
      username: false,
      location: false,
    };
    initialLocation.current = '';
  };

  const handleLocationFieldChange = address => {
    const isUpdated = address !== initialLocation.current;

    handleUpdatedField('location', isUpdated);
  };

  const updateMyLocation = address => {
    handleLocationFieldChange(address);
    setMyLocation(address);
  };

  useEffect(() => {
    const isUpdated = displayName !== username;

    handleUpdatedField('username', isUpdated);
  }, [displayName, username]);

  useEffect(() => {
    setEditProfileCover(prevState => (errors?.city ? !prevState : false));
  }, [errors?.city]);

  const updateMyUsername = e => {
    const { value } = e.target;

    setDisplayName(value);

    if (value !== displayName) {
      setIsNameChanged(true);
    }
  };

  const postSubmit = e => {
    const autocompleteLocation = document.getElementById('autocompleteLocation') as HTMLInputElement | null;

    if (autocompleteLocation) {
      autocompleteLocation.value = '';
    }

    toggleEditable(e);
    handleResetUpdatedFields();
  };

  const handleSubmit = async e => {
    setIsSubmitted(true);

    if (updatedLocation !== undefined) {
      const newLocation = extractLocationData(updatedLocation);
      const currentCountry = account?.country ?? profile?.data?.country;
      const currentCity = account?.city ?? profile?.data?.city;

      await dispatch(
        updateProfileCover({
          username: displayName,
          city: newLocation.city,
          placeId: newLocation.placeId,
          country: newLocation.country,
          region: newLocation.region,
          latitude: newLocation.latitude,
          longitude: newLocation.longitude,
          updatedFields: updatedFields.current,
        })
      ).then(() => {
        postSubmit(e);

        if (newLocation.country !== currentCountry || newLocation.city !== currentCity) {
          dispatch(resetDiscoverModeAccountsAction());
          setLocationUpdatedForConsent(true);
        }
      });
    } else {
      updatedFields.current.location = false;

      await dispatch(
        updateProfileCover({
          username: displayName,
          city: account.city ? account.city : profile.data.city,
          country: account.country ? account.country : profile.data.country,
          region: account.region ? account.region : profile.data.region,
          updatedFields: updatedFields.current,
        })
      ).then(() => postSubmit(e));
    }

    if (isNameChanged) {
      setIsEditableName(false);
      confirmSuccessModal({
        dataTestID: 'invalid-file-selected-modal',
        modalContentLabel: 'Success!',
        customOverlayStyle: { zIndex: 10002 },
        customHeaderDiv: { padding: 0, marginTop: 40, marginBottom: 0, [breakpoints.down(768.05)]: { marginTop: 0 } },
        customContentBodyStyle: { padding: 30, background: getBackgroundTheme().block2 },
        customContainerDiv: { padding: 0 },
        customCloseBtnStyle: {
          top: 0,
          right: 0,
          textAlign: 'right',
          padding: '30px 30px 0 30px',
          width: '100%',
          height: 'auto',
        },
        customLabel: {
          fontFamily: FONT_FAMILIES.secondary,
          fontWeight: 400,
          fontSize: '24px',
          marginBottom: 20,
          lineHeight: '18px',
          letterSpacing: -0.3,
          color: isDarkMode() ? typography['dark'].heading : '#333333',
          [breakpoints.down(639.05)]: { fontSize: '24px', lineHeight: '18px' },
        },
        confirmationProps: {
          message: (
            <>
              <ModalText>
                First Name has been changed successfully. This can only be done once. Contact support for any help
                afterwards.
              </ModalText>
            </>
          ),
          title: 'Success!',
          confirmBtnType: 'secondaryBlue',
          confirmText: 'Close',
          confirmAction: () => resetModal(),
          cancelAction: () => resetModal(),
        },
      });
    }
  };

  const handleCancel = e => {
    // need to clear the fields if user select cancel button
    dispatch(clearErrors());
    toggleEditable(e);
    setDisplayName(username);
    handleResetUpdatedFields();
    setUpdatedLocation(undefined);

    setMyLocation(
      getLocationDisplay(
        account.city ? account.city : profile.data.city,
        account.region ? account.region : profile.data.region,
        account.country ? account.country : profile.data.country
      )
    );
  };

  const getLocationDisplay = (city, region, country) => {
    return normalizeLocationDisplay(city, region, country);
  };

  return (
    <BaseCover
      className="my-profile-cover"
      onChangeAddress={updateMyLocation}
      onSelectLocation={locationSelectHandler}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      updateUsername={updateMyUsername}
      displayName={displayName}
      myLocation={myLocation}
      editProfileCover={editProfileCover}
      toggleEditable={toggleEditable}
      profileImg={renderAvatar(myPhoto)}
      editable={editable}
      isMigration={isMigration}
      errors={errors}
      isEditableName={isEditableName}
      isSelfieVerified={nestedProfile && nestedProfile?.data?.selfie_verified}
      {...props}
    >
      <CoverContent>
        {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
        <ProfileCompletionProgress type="complete" onboarding={account} profilev2 />
      </CoverContent>
    </BaseCover>
  );
};

export default React.memo(MyProfileCover, areEqual);
